import type { Counterparty, CreateCounterpartyRequest } from "@payezy/api-client/public";
import { archiveCounterpartyMutation, createCounterpartyMutation, getPaymentLimitsOptions, listCounterpartiesOptions, listCounterpartiesQueryKey } from "@payezy/api-client/public/query";
import { Alert, Badge, Button, Card, Dialog, EmptyState, Field, Input, Menu, MenuItem, PageHeader, Select, Spinner, Table, TD, TH, THead, TR, Tabs, formatDate, toast } from "@payezy/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark, MoreHorizontal, Plus, Send, Wallet } from "lucide-react";
import { useState } from "react";
import { handleAuthProblem, problemMessage } from "../lib/api";
import { useOrg } from "../lib/org";
import { railName } from "../lib/payments";
import { SendMoneyDialog } from "./SendMoney";

const bankRails = ["ach_credit", "same_day_ach_credit", "wire", "fednow_credit"] as const;
type BankRail = (typeof bankRails)[number];

const screeningTone = { clear: "success", review: "warning", blocked: "danger" } as const;

const networkNames: Record<string, string> = {
  base: "Base", base_sepolia: "Base Sepolia (testnet)", ethereum: "Ethereum", sepolia: "Sepolia (testnet)",
  polygon: "Polygon", arbitrum: "Arbitrum", solana: "Solana", solana_devnet: "Solana Devnet (testnet)",
};
export const networkName = (n: string) => networkNames[n] ?? n;

function AddRecipientDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const org = useOrg();
  const qc = useQueryClient();
  const limits = useQuery(getPaymentLimitsOptions({ path: { orgId: org.org_id } }));
  const [type, setType] = useState<"bank" | "wallet">("bank");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"business" | "individual" | "self">("business");
  const [email, setEmail] = useState("");
  const [bank, setBank] = useState({ bank_name: "", routing_number: "", account_number: "", account_type: "checking" as "checking" | "savings" });
  const [rails, setRails] = useState<BankRail[]>(["ach_credit", "same_day_ach_credit"]);
  const [addr, setAddr] = useState({ street_line_1: "", city: "", state: "", zip: "" });
  const [bankAddr, setBankAddr] = useState({ street_line_1: "", city: "", state: "", zip: "" });
  const [wallet, setWallet] = useState({ network: "", address: "", label: "" });
  const networks = limits.data?.networks ?? [];
  const create = useMutation({
    ...createCounterpartyMutation(),
    onSuccess: () => {
      toast.success("Recipient added");
      void qc.invalidateQueries({ queryKey: listCounterpartiesQueryKey({ path: { orgId: org.org_id } }) });
      onOpenChange(false);
    },
    onError: (e) => { if (!handleAuthProblem(e)) toast.error(problemMessage(e)); },
  });
  const wire = rails.includes("wire");
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: CreateCounterpartyRequest = { name, kind, ...(email ? { email } : {}) };
    if (type === "bank") {
      body.bank = { ...bank, owner_name: name, rails, ...(wire ? { beneficiary_address: addr, bank_address: bankAddr } : {}) };
    } else {
      body.wallet = { network: wallet.network || networks[0] || "", address: wallet.address, ...(wallet.label ? { label: wallet.label } : {}) };
    }
    create.mutate({ path: { orgId: org.org_id }, body });
  };
  const toggleRail = (r: BankRail) => setRails((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Add recipient" description="Bank details are encrypted; only the last four digits are shown again." wide>
      <form onSubmit={submit} className="grid gap-4">
        <Tabs value={type} onValueChange={(v) => setType(v as "bank" | "wallet")} tabs={[{ value: "bank", label: "US bank account" }, { value: "wallet", label: "Stablecoin wallet" }]} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name"><Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Supplies LLC" /></Field>
          <Field label="Type">
            <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="business">Business</option>
              <option value="individual">Individual</option>
              <option value="self">My own account</option>
            </Select>
          </Field>
        </div>
        <Field label="Email (optional)" hint="We can notify the recipient when a payment is sent."><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        {type === "bank" ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Bank name"><Input value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} placeholder="Chase" /></Field>
              <Field label="Account type">
                <Select value={bank.account_type} onChange={(e) => setBank({ ...bank, account_type: e.target.value as "checking" | "savings" })}>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </Select>
              </Field>
              <Field label="Routing number"><Input required inputMode="numeric" value={bank.routing_number} onChange={(e) => setBank({ ...bank, routing_number: e.target.value })} placeholder="9 digits" /></Field>
              <Field label="Account number"><Input required inputMode="numeric" autoComplete="off" value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} /></Field>
            </div>
            <Field label="Payment methods this account accepts">
              <div className="flex flex-wrap gap-3">
                {bankRails.map((r) => (
                  <label key={r} className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={rails.includes(r)} onChange={() => toggleRail(r)} /> {railName(r)}
                  </label>
                ))}
              </div>
            </Field>
            {wire && (
              <div className="grid gap-3 rounded-lg bg-ink-50 p-3">
                <p className="text-xs font-medium text-ink-600">Wires need the recipient's and the bank's addresses</p>
                <AddressFields label="Recipient address" value={addr} onChange={setAddr} />
                <AddressFields label="Bank address" value={bankAddr} onChange={setBankAddr} />
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Network">
              <Select value={wallet.network || networks[0] || ""} onChange={(e) => setWallet({ ...wallet, network: e.target.value })}>
                {networks.map((n) => <option key={n} value={n}>{networkName(n)}</option>)}
              </Select>
            </Field>
            <Field label="Label (optional)"><Input value={wallet.label} onChange={(e) => setWallet({ ...wallet, label: e.target.value })} placeholder="Treasury" /></Field>
            <Field label="Wallet address" className="col-span-2"><Input required className="font-mono" value={wallet.address} onChange={(e) => setWallet({ ...wallet, address: e.target.value })} placeholder="0x…" /></Field>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" loading={create.isPending}>Add recipient</Button>
        </div>
      </form>
    </Dialog>
  );
}

type Addr = { street_line_1: string; city: string; state: string; zip: string };

function AddressFields({ label, value, onChange }: { label: string; value: Addr; onChange: (a: Addr) => void }) {
  return (
    <div className="grid grid-cols-6 gap-2">
      <Field label={label} className="col-span-6"><Input required value={value.street_line_1} onChange={(e) => onChange({ ...value, street_line_1: e.target.value })} placeholder="Street" /></Field>
      <Input required className="col-span-3" value={value.city} onChange={(e) => onChange({ ...value, city: e.target.value })} placeholder="City" />
      <Input required className="col-span-1" maxLength={2} value={value.state} onChange={(e) => onChange({ ...value, state: e.target.value.toUpperCase() })} placeholder="ST" />
      <Input required className="col-span-2" value={value.zip} onChange={(e) => onChange({ ...value, zip: e.target.value })} placeholder="ZIP" />
    </div>
  );
}

export function accountSummary(c: Counterparty): string {
  const a = c.accounts[0];
  if (!a) return "—";
  if (a.type === "bank") return `${a.bank_name || "Bank"} ••${a.account_last4} · ${a.rails.map(railName).join(", ")}`;
  return `${networkName(a.network ?? "")} · ${a.address?.slice(0, 6)}…${a.address?.slice(-4)}`;
}

export function RecipientsPage() {
  const org = useOrg();
  const qc = useQueryClient();
  const q = useQuery(listCounterpartiesOptions({ path: { orgId: org.org_id } }));
  const [adding, setAdding] = useState(false);
  const [sendTo, setSendTo] = useState<Counterparty | null>(null);
  const archive = useMutation({
    ...archiveCounterpartyMutation(),
    onSuccess: () => { toast.success("Recipient removed"); void qc.invalidateQueries({ queryKey: listCounterpartiesQueryKey({ path: { orgId: org.org_id } }) }); },
    onError: (e) => { if (!handleAuthProblem(e)) toast.error(problemMessage(e)); },
  });
  const canManage = org.can("counterparties:manage");
  const canSend = org.can("payments:create") && org.org_status === "active";
  return (
    <>
      <PageHeader
        title="Recipients"
        description="People, businesses, and wallets you pay"
        actions={canManage && <Button onClick={() => setAdding(true)}><Plus className="size-4" /> Add recipient</Button>}
      />
      {q.isError && <Alert tone="danger">{problemMessage(q.error)}</Alert>}
      <Card>
        {q.isPending ? (
          <div className="flex justify-center p-8"><Spinner /></div>
        ) : q.data?.items.length ? (
          <Table>
            <THead><tr><TH>Name</TH><TH>Account</TH><TH>Screening</TH><TH>Added</TH><TH className="w-10" /></tr></THead>
            <tbody>
              {q.data.items.map((c) => (
                <TR key={c.id}>
                  <TD>
                    <span className="inline-flex items-center gap-2 font-medium">
                      {c.accounts[0]?.type === "wallet" ? <Wallet className="size-4 text-ink-500" /> : <Landmark className="size-4 text-ink-500" />}
                      {c.name}
                    </span>
                  </TD>
                  <TD className="text-ink-600">{accountSummary(c)}</TD>
                  <TD><Badge tone={screeningTone[c.screening_status]}>{c.screening_status === "clear" ? "Clear" : c.screening_status === "review" ? "In review" : "Blocked"}</Badge></TD>
                  <TD className="text-ink-500">{formatDate(c.created_at)}</TD>
                  <TD>
                    <div className="flex items-center justify-end gap-1">
                      {canSend && c.screening_status === "clear" && (
                        <Button size="sm" variant="secondary" onClick={() => setSendTo(c)}><Send className="size-3.5" /> Pay</Button>
                      )}
                      {canManage && (
                        <Menu trigger={<Button size="icon" variant="ghost" aria-label="More"><MoreHorizontal className="size-4" /></Button>}>
                          <MenuItem onSelect={() => archive.mutate({ path: { orgId: org.org_id, counterpartyId: c.id } })}>Remove</MenuItem>
                        </Menu>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState
            icon={<Wallet className="size-6" />}
            title="No recipients yet"
            description="Add a US bank account or a stablecoin wallet to start sending money."
            action={canManage && <Button onClick={() => setAdding(true)}><Plus className="size-4" /> Add recipient</Button>}
          />
        )}
      </Card>
      {adding && <AddRecipientDialog open onOpenChange={setAdding} />}
      {sendTo && <SendMoneyDialog recipient={sendTo} onClose={() => setSendTo(null)} />}
    </>
  );
}
